import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { format } from 'date-fns'

import { EMPTY_LIST } from '../../../../../constants/common'
import { DATE_FULL_FORMAT_FROM_TIME } from '../../../../../constants/date'
import { HTML_LINK_TARGET } from '../../../../../constants/html'
import {
  useInitData,
  useIsBuildingGroupProfileNGAvailable,
  useLinkLikeClick,
  useScreen,
  useUkTimeZone,
  useUserData,
} from '../../../../../hooks'
import { toUkTimezoneDate } from '../../../../../utils/date'
import { getPostProfilePathname } from '../../../../../utils/link'
import { isStaffUser } from '../../../../../utils/user'
import { FloatingTooltip } from '../../../../Floating'
import { Link } from '../../../../Link'
import {
  getMultiValuedListVisibleCountByScreen,
  MultiValuedList,
} from '../../../../MultiValuedList'
import Text from '../../../../Text'
import {
  isRequestFeedbackType,
  isRequestPostType,
  isWelcomePostType,
} from '../../../Newsboard.utils'
import { NewsboardMessageText } from '../../../NewsboardMessageText'
import { NewsboardPostRating } from '../NewsboardPostRating'

import { ListItem } from './ListItem'
import { ListItems } from './ListItems'
import { More } from './More'
import { MoreItem } from './MoreItem'
import {
  filterByTitle,
  getListItemLink,
  getRatingPostText,
  isHiddenMessagePostType,
  parseAudience,
  parseBuildings,
  parseStatus,
} from './NewsboardPostBodyMessage.utils'
import { NewsboardPostBodyMessageRequests } from './NewsboardPostBodyMessageRequests'

import * as styles from './NewsboardPostBodyMessage.module.css'

function NewsboardPostBodyMessage({ data, unread, onReload }) {
  const { t } = useTranslation()

  const ukLocaleTimeZone = useUkTimeZone()

  const user = useUserData()

  const { availableSections } = useInitData()

  const isBuildingGroupProfileNGAvailable =
    useIsBuildingGroupProfileNGAvailable()

  const isStaff = isStaffUser(user)

  const {
    audience = EMPTY_LIST,
    building_groups,
    buildings,
    created,
    personal_status,
    request_dump: requestDump,
    requests,
    text,
    title,
    uuid,
  } = data

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const { width: screenWidth } = useScreen()

  const multiValuedListVisibleCount =
    getMultiValuedListVisibleCountByScreen(screenWidth)

  const requestStatusType = isRequestPostType(data)

  const ratingType = isRequestFeedbackType(data)

  const isWelcomePost = isWelcomePostType(data)

  const messageContent = ratingType ? getRatingPostText(requests) : text

  function getTagItemLink({ id, to }) {
    return getListItemLink({
      availableSections,
      id,
      isBuildingGroupProfileNGAvailable,
      to,
    })
  }

  function handleClickItem({ id, to }) {
    if (to) {
      handleClick({
        link: getTagItemLink({ id, to }),
        target: HTML_LINK_TARGET.blank,
      })
    }
  }

  function handleMouseDownItem({ event, id, to }) {
    if (to) {
      handleMouseDown({ event, link: getTagItemLink({ id, to }) })
    }
  }

  const buildingItems = parseBuildings({
    audience,
    buildingGroups: building_groups,
    buildings,
    t,
  })

  const audienceItems = parseAudience({ audience, isStaff, t })

  const statusItems = parseStatus(personal_status)

  const audienceTagItems = [...buildingItems, ...audienceItems, ...statusItems]

  const dateCreated = created
    ? format(
        toUkTimezoneDate(created, ukLocaleTimeZone.timezone),
        DATE_FULL_FORMAT_FROM_TIME
      )
    : null

  const hideMessage = isHiddenMessagePostType(data)

  const requestType = requestStatusType || ratingType

  const titleClassName = classNames(
    styles.title,
    {
      [styles.hoverableTitle]: !!uuid,
    },
    { [styles.unreadTitle]: unread }
  )

  return (
    <div className={styles.NewsboardPostBodyMessage}>
      {!requestStatusType ? (
        <FloatingTooltip
          content={t('Newsboard.OpenPostInNewTabKey')}
          disabled={ratingType}
        >
          {uuid ? (
            <Link
              reloadDocument
              className={styles.link}
              rel='noreferrer'
              target={HTML_LINK_TARGET.blank}
              to={getPostProfilePathname({ availableSections, id: uuid })}
            >
              <Text className={titleClassName}>
                {ratingType ? t('Newsboard.RatingTitleKey') : title}
              </Text>
            </Link>
          ) : (
            <Text className={titleClassName}>{title}</Text>
          )}
        </FloatingTooltip>
      ) : null}
      {ratingType ? (
        <NewsboardPostRating
          request={requests[0]}
          onUpdateListData={onReload}
        />
      ) : null}
      {!hideMessage ? (
        <>
          <NewsboardMessageText
            messageContent={messageContent}
            unread={unread}
          />
          {!requestType && !isWelcomePost ? (
            <MultiValuedList
              Item={ListItem}
              Items={ListItems}
              More={More}
              MoreItem={MoreItem}
              className={styles.multiValuedList}
              filter={filterByTitle}
              items={audienceTagItems}
              visibleCount={multiValuedListVisibleCount}
              onClickItem={handleClickItem}
              onMouseDownItem={handleMouseDownItem}
            />
          ) : null}
        </>
      ) : null}
      {requests?.length > 0 ? (
        <NewsboardPostBodyMessageRequests
          ratingType={ratingType}
          requestDump={requestDump}
          requests={requests}
          requestStatusType={requestStatusType}
          unread={unread}
          visibleCount={multiValuedListVisibleCount}
        />
      ) : null}
      {dateCreated ? (
        <Text
          isSecondary
        >{`${t('Newsboard.PostCreatedAt')}  ${dateCreated}`}</Text>
      ) : null}
    </div>
  )
}

export default NewsboardPostBodyMessage
